import { ChangeEvent, FC, useCallback, useState, useMemo, useRef, useEffect } from "react";

import { i18n, useTranslation } from "next-i18next";

import TextInput from "@components/TextInput";
import { withCompositeFormikField } from "@lib/withFormikField";

import countries from "./data/countries";
import PhoneCodeSelect from "./PhoneCodeSelect";
import type { TPhoneCodeOption, TPhoneInputInternalState, TPhoneInputProps } from "./types";
import { isPhoneNumberValid, preparePhoneOptionsList } from "./utils";
import { findCountryPhoneCode } from "./utils/findCountryPhoneCode";
import { getNumberWithoutPefixAndCountryCode } from "./utils/getNumberWithoutPefixAndCountryCode";
import normalizePhoneNumber from "./utils/normalizePhoneNumber";

const getInitialState = (options: TPhoneCodeOption[], defaultValue?: string): TPhoneInputInternalState => {
  const defaultState = {
    country: options[0].shortName,
    phoneNumber: "",
  };

  if (!defaultValue) {
    return defaultState;
  }

  const [phoneNumberWithoutPrefix, countryCode] = getNumberWithoutPefixAndCountryCode(defaultValue);
  if (!phoneNumberWithoutPrefix || !countryCode) {
    return defaultState;
  }

  return {
    country: countryCode,
    phoneNumber: phoneNumberWithoutPrefix,
  };
};

const PhoneInput: FC<TPhoneInputProps> = ({
  id,
  isFormik = true,
  setFieldValue,
  pinnedPhoneCodes,
  defaultValue,
  defaultCountry,
  value,
  onChange,
  onBlur,
  isDisabled,
  message,
  isInvalid,
  onPhoneCodeChange,
  ...rest
}) => {
  const { t } = useTranslation();
  const options = useMemo(
    () => preparePhoneOptionsList(countries, pinnedPhoneCodes, defaultCountry),
    [pinnedPhoneCodes, defaultCountry]
  );

  const [innerValue, setInnerValue] = useState(getInitialState(options, defaultValue));

  const [innerErrorMessage, setInnerErrorMessage] = useState("");
  const valueChangeRef = useRef<string>();

  const updatePhoneValues = useCallback(
    (fullPhoneNumber) => {
      const [phoneNumber, country] = getNumberWithoutPefixAndCountryCode(fullPhoneNumber);
      setInnerValue((oldInnerValue) => ({
        ...oldInnerValue,
        ...(country && { country }),
        ...(phoneNumber && { phoneNumber }),
      }));
    },
    [setInnerValue]
  );

  useEffect(() => {
    // no need to update an undefined value or a value that has been intenally set.
    const hasNoValue = typeof value === "undefined";
    const hasBeenInternallySet = value === valueChangeRef.current;
    if (hasNoValue || hasBeenInternallySet) {
      return;
    }
    updatePhoneValues(value);
  }, [updatePhoneValues, value]);

  useEffect(() => {
    /*
      This is needed since sometimes defaultCountry is loaded from localStorage
      and it is delayed after initial render
     */
    setInnerValue((oldState) => {
      if (oldState.phoneNumber) {
        return oldState;
      }

      return { ...oldState, country: options[0].shortName };
    });
  }, [defaultCountry, options]);

  const handleSave = useCallback(
    (changedValue: string) => {
      valueChangeRef.current = changedValue;
      if (onChange) {
        onChange(changedValue);
      }
      if (setFieldValue) {
        setFieldValue(changedValue);
      }
    },
    [setFieldValue, onChange]
  );

  const handlePhoneCodeChanged = useCallback(
    (country: string) => {
      const phoneCode = findCountryPhoneCode(country);
      if (onPhoneCodeChange) {
          onPhoneCodeChange(country);
      }
      setInnerValue((oldValue) => ({ ...oldValue, country }));

      const shouldChange = innerValue.phoneNumber.length > 0;
      if (!shouldChange) {
        return;
      }

      const phoneValue = normalizePhoneNumber(phoneCode + innerValue.phoneNumber);
      const normalizedLocalNumber = phoneCode && phoneValue.startsWith(phoneCode)
        ? phoneValue.substring(phoneCode.length)
        : phoneValue;
      setInnerValue((oldValue) => ({ ...oldValue, phoneNumber: normalizedLocalNumber }));

      handleSave(phoneValue);
    },
    [innerValue.phoneNumber, handleSave]
  );

  const handlePhoneNumberChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const phoneNumber = evt.target.value;
      setInnerValue((oldValue) => ({ ...oldValue, phoneNumber }));

      if (!phoneNumber) {
        handleSave("");
        return;
      }

      const phoneCode = innerValue.country ? findCountryPhoneCode(innerValue.country) : options[0].code;
      const phoneValue = normalizePhoneNumber(phoneCode + phoneNumber);
      const normalizedLocalNumber = phoneCode && phoneValue.startsWith(phoneCode)
        ? phoneValue.substring(phoneCode.length)
        : phoneValue;
      setInnerValue((oldValue) => ({ ...oldValue, phoneNumber: normalizedLocalNumber }));

      handleSave(phoneValue);
    },
    [options, handleSave, innerValue.country]
  );

  const handlePhoneInputBlur = useCallback(
    (evt: any) => {
      onBlur?.(evt);
      const phoneCode = innerValue.country ? findCountryPhoneCode(innerValue.country) : options[0].code;
      let phoneValue = phoneCode + innerValue.phoneNumber;
      phoneValue = normalizePhoneNumber(phoneValue);

      if (!isFormik) {
        setInnerErrorMessage(!isPhoneNumberValid(phoneValue) ? t("common-phone_input-invalid_format") : "");
      }
    },
    [innerValue.country, innerValue.phoneNumber, isFormik, onBlur, t, options]
  );

  const phoneCodeSelect = (
    <PhoneCodeSelect
      id={id}
      options={options}
      country={innerValue.country}
      onChange={handlePhoneCodeChanged}
      isDisabled={isDisabled}
    />
  );
  return (
    <TextInput
      id={id}
      value={innerValue.phoneNumber}
      onChange={handlePhoneNumberChange}
      onBlur={handlePhoneInputBlur}
      isDisabled={isDisabled}
      isInvalid={!!innerErrorMessage || isInvalid}
      message={innerErrorMessage || message}
      {...rest}
      isFormik={false}
      leftAddon={phoneCodeSelect}
      leftAddonOffset="5.5rem !important"
      leftSpacing="0.25rem !important"
    />
  );
};

const validate = (phoneNumber?: string): string | undefined => {
  if (!phoneNumber) {
    return undefined;
  }

  const isValid = isPhoneNumberValid(phoneNumber);
  return isValid ? undefined : i18n?.t("common-phone_input-invalid_format");
};

export default withCompositeFormikField({ hasSetFieldValue: true, validate })(PhoneInput);
